import { useDeleteDesignsByDesignMutation, useGetAuthSessionQuery, useGetDesignsByDesignQuery, useGetProductVariantsQuery, useGetProductsQuery } from '@/api/serverApi'
import ObjectExplorer from '@/components/3d/ObjectExplorer'
import PropertiesContainer from '@/components/3d/PropertiesContainer'
import ViewerScene from '@/components/3d/ViewerScene'
import AddProductPicker from '@/components/3d/product-picker/AddProductPicker'
import RenameDesignForm from '@/components/forms/designs/RenameDesignForm'
import ReassignDesignModal from '@/components/modals/designs/ReassignDesignModal'
import { AppDispatch, RootState } from '@/stores'
import { setActiveDesign, setSelectedComponent } from '@/stores/active-design'
import { resetCamera, setSelectedCameraControlType } from '@/stores/camera-control-type'
import { setProductVariants, setProducts } from '@/stores/products'
import { BrandIcon, CameraRefocusIcon, ChevronIcon, CursorIcon, DeleteModal, Dropdown, Meta, PlusIcon, RotateCameraIcon } from '@cfs-fixings/shared'
import Button from '@cfs-fixings/shared/src/components/button'
import toastHelper from '@cfs-fixings/shared/src/helpers/toastHelper'
import classNames from 'classnames'
import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'
import loadingSpinnerImage from '@/assets/clariti-loading-spinner.gif'
import PreviewReportOverlay from '@/components/modals/reports/PreviewReportOverlay'
import { generateReportAction } from '@/stores/report-preview'
import ProfileDropdown from '@/components/header/ProfileDropdown'
import useDesignValidity from '@/hooks/useDesignValidity'
import { PropertyTabs, setCurrentPropertiesTab } from '@/stores/selected-properties-tab'

const textSpec = {
  renameButtonLabel: 'Rename',
  reassignButtonLabel: 'Move to project',
  deleteButtonLabel: 'Delete design',
  deleteModal: {
    successMessage: {
      title: 'Design deleted successfully',
      message: 'Design has been removed from the system',
    },
    errorMessage: {
      title: 'Failed to delete design',
      message: 'Design cannot be deleted at this time, please try again later',
    },
    modal: {
      title: 'Delete Design',
      message: 'Are you sure you want to delete this design? This action cannot be undone.',
    },
    button: {
      text: 'Delete',
    },
  },
}

const ViewDesignPage = () => {
  const { designId } = useParams()
  const dispatch = useDispatch<AppDispatch>()
  const navigate = useNavigate()
  const { checkValidity } = useDesignValidity()

  const [isDragging, setIsDragging] = useState(false)
  const [isMouseDown, setIsMouseDown] = useState(false)
  const [isFirstLoad, setIsFirstLoad] = useState(true)
  const [isProductPickerOpen, setIsProductPickerOpen] = useState(false)
  const [isRenaming, setIsRenaming] = useState(false)
  const [isReassignOpen, setIsReassignOpen] = useState(false)
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const [isPreviewReportOpen, setIsPreviewReportOpen] = useState(false)

  const isReplacing = useSelector((state: RootState) => state.activeDesign.replacing)
  const activeDesign = useSelector((state: RootState) => state.activeDesign.design)
  const selectedCameraControlType = useSelector((state: RootState) => state.selectedCameraControlType.option)

  useEffect(() => {
    dispatch(setSelectedCameraControlType('cursor'))
  }, [dispatch])

  const [deleteMutation, { isLoading: isDeleteSubmitting }] = useDeleteDesignsByDesignMutation()

  const { data: user } = useGetAuthSessionQuery()
  const { data: productVariants, isFetching: isFetchingProductVariants } = useGetProductVariantsQuery({})
  const { data: products, isFetching: isFetchingProducts } = useGetProductsQuery({})
  const { data: design, isFetching: isFetchingDesign } = useGetDesignsByDesignQuery({
    design: designId!,
  }, { skip: !isFirstLoad })

  const onDelete = () => {
    if (design?.data === undefined) {
      return
    }

    deleteMutation({ body: {}, design: design.data.id }).unwrap().then(() => {
      setIsDeleteModalOpen(false)
      navigate('/designs')
      toastHelper.success(
        textSpec.deleteModal.successMessage.title,
        textSpec.deleteModal.successMessage.message,
      )

    }).catch(() => {
      toastHelper.error(
        textSpec.deleteModal.errorMessage.title,
        textSpec.deleteModal.errorMessage.message,
      )
    },
    )
  }


  const isFetching = isFetchingDesign || isFetchingProducts || isFetchingProductVariants

  useEffect(() => {
    if (products && productVariants && design) {
      dispatch(setActiveDesign(design.data))
      dispatch(setProducts(products.data))
      dispatch(setProductVariants(productVariants.data))

      setIsFirstLoad(false)
    }
  }, [design, products, productVariants, dispatch])

  const handleMouseMove = () => {
    if (isMouseDown) {
      return setIsDragging(true)
    }

    setIsDragging(false)
  }

  const handleResetToDesignTab = () => {
    if (!isDragging) {
      dispatch(setSelectedComponent(''))
      dispatch(setCurrentPropertiesTab(PropertyTabs.Results))
    }
  }

  return <>
    <Meta title={design?.data.name} />

    <nav className="fixed top-0 inset-x-0 h-[48px] bg-black text-white z-10">
      <div className="flex justify-center items-center absolute w-full top-0 h-full">
        {
          design?.data
          && (
            <div className="flex flex-row items-center gap-2">
              <RenameDesignForm
                design={design!.data}
                isFormActive={isRenaming}
                onCompleted={() => {
                  setIsRenaming(false)
                }}
              />
              {
                !isRenaming
                && (
                  <Dropdown
                    buttonContentOverride={<ChevronIcon className="w-[10px] h-[10px]" />}
                    buttonClassName="hover:!bg-gray-600"
                  >
                    <Dropdown.Item
                      onClick={() => {
                        setIsRenaming(true)
                      }}
                    >
                      {textSpec.renameButtonLabel}
                    </Dropdown.Item>
                    <Dropdown.Item onClick={() => setIsReassignOpen(true)}>
                      {textSpec.reassignButtonLabel}
                    </Dropdown.Item>
                    <Dropdown.Separator />
                    <Dropdown.Item onClick={() => setIsDeleteModalOpen(true)}>
                      {textSpec.deleteButtonLabel}
                    </Dropdown.Item>
                  </Dropdown>
                )
              }
            </div>
          )
        }

      </div>

      <div className="flex w-full justify-between items-center h-full px-[20px]">
        <div className="flex flex-row gap-5 items-center">
          <Button
            variant="dark"
            size="xs"
            onClick={() => navigate('/designs')}
            toolTip="Home"
          >
            <BrandIcon className="w-[20px] h-[20px]" />
          </Button>
          <div className="flex flex-row items-center gap-5">
            <div className="w-[1px] h-[24px] bg-gray-700"></div>
            <Button
              variant="dark"
              size="xs"
              onClick={() => dispatch(setSelectedCameraControlType('cursor'))}
              className={selectedCameraControlType === 'cursor' ? 'bg-primary-600 hover:bg-primary-800' : ''}
              toolTip="Select and move"
            >
              <CursorIcon className={classNames('w-[24px] h-[24px] text-white', {
                '!text-black': selectedCameraControlType === 'cursor',
              })}
              />
            </Button>
            <Button
              variant="dark"
              size="xs"
              onClick={() => dispatch(setSelectedCameraControlType('rotate'))}
              className={selectedCameraControlType === 'rotate' ? 'bg-primary-600 hover:bg-primary-800' : ''}
              toolTip="Rotate"
            >
              <RotateCameraIcon className={classNames('w-[24px] h-[24px] text-white', {
                '!text-black': selectedCameraControlType === 'rotate',
              })}
              />
            </Button>
            <Button
              variant="dark"
              size="xs"
              onClick={() => dispatch(resetCamera())}
              toolTip="Reset view"
            >
              <CameraRefocusIcon className="w-[24px] h-[24px] text-white"  />
            </Button>
            <Button
              variant="dark"
              size="xs"
              onClick={() => setIsProductPickerOpen(true)}
              toolTip="Add component"
            >
              {/* Ensures the button still fits the same sizes as the others despite the smaller icon */}
              <div className="h-6 w-6 flex items-center justify-center">
                <PlusIcon className="w-3 h-3 text-white"  />
              </div>
            </Button>
          </div>
        </div>

        <div className="flex flex-row gap-5">
          <Button
            size="sm"
            variant="secondary"
            onClick={() => {
              if (!checkValidity()) {
                toastHelper.error('Invalid design', 'Please ensure the design has at least two anchors and one T-bolt before generating a report')
                return
              }

              dispatch(generateReportAction({
                details: {},
              }))
              setIsPreviewReportOpen(true)
            }}
          >
            Preview Report
          </Button>
          <ProfileDropdown user={user?.data} size="small" />
        </div>


        <AddProductPicker isOpen={isProductPickerOpen} onClose={() => setIsProductPickerOpen(false)}  />
      </div>
    </nav>

    {
      !activeDesign || isFetching
        ? (
          <div className="fixed inset-0 flex items-center justify-center h-full">
            <img
              src={loadingSpinnerImage}
              className="h-24 w-24"
              alt="Loading spinner"
            />
          </div>
        )
        : <>
          <div className="w-full">
            <section className="h-[calc(100vh-48px)] mt-[48px] overflow-y-auto w-[240px] bg-white border-r border-gray-100">
              <ObjectExplorer />
            </section>
          </div>

          <div
            className="fixed inset-y-0 left-[240px] top-[48px] right-[285px] cursor-default"
            onMouseDown={() => setIsMouseDown(true)}
            onMouseUp={() => setIsMouseDown(false)}
            onMouseMove={handleMouseMove}
            onDoubleClick={handleResetToDesignTab}
            role="button"
            tabIndex={0}
            onKeyDown={() => { /** Do Nothing */ }}
          >
            {
              isReplacing
                ? (
                  <div className="fixed inset-0 flex items-center justify-center h-full">
                    <img
                      src={loadingSpinnerImage}
                      className="h-24 w-24"
                      alt="Loading spinner"
                    />
                  </div>
                )
                : null
            }
            {
              !isReplacing
                && <ViewerScene />
            }
          </div>

          <div className="w-full overflow-x-hidden">
            <section className="h-[calc(100vh-48px)] mt-[48px] overflow-y-auto w-[285px] bg-white border-l border-gray-100 ml-auto">
              <PropertiesContainer />
            </section>
          </div>
        </>
    }
    {
      design?.data
      && (
        <ReassignDesignModal
          design={design?.data}
          isOpen={isReassignOpen}
          onClose={() => setIsReassignOpen(false)}
          onSuccess={() => {
            setIsReassignOpen(false)
          }}
        />
      )
    }
    {
      design?.data
      && (
        <DeleteModal
          isOpen={isDeleteModalOpen}
          onClose={() => setIsDeleteModalOpen(false)}
          onConfirm={onDelete}
          title={textSpec.deleteModal.modal.title}
          isLoading={isDeleteSubmitting}
          message={textSpec.deleteModal.modal.message}
        />
      )
    }

    {
      designId
        && (
          <PreviewReportOverlay
            isOpen={isPreviewReportOpen}
            onClose={() => {
              setIsPreviewReportOpen(false)
            }}
            designId={designId}
          />
        )
    }
  </>
}

export default ViewDesignPage
